import { useMemo, useState } from "react";
import { useLocale } from "../hooks/useLocale";
import type { PivotConfig } from "../api/types";
import { TablePagination, type TablePaginationProps } from "./TablePagination";
import styles from "./TableView.module.css";

interface Props {
  columns: string[];
  rows: unknown[][];
  pivot: PivotConfig;
  pagination?: TablePaginationProps;
  dimensionLabel?: (field: string) => string;
  metricLabel?: (field: string) => string;
}

interface PivotShape {
  rowDimNames: string[];
  colDimNames: string[];
  rowKeys: string[][];
  colKeys: string[][];
  // cell key: rowKey (joined) + colKey (joined) — value.
  cells: Map<string, unknown>;
}

const METRIC_AXIS = "$metric";

function joinKey(parts: string[]): string {
  return parts.join("\x1f");
}

function build(columns: string[], rows: unknown[][], pc: PivotConfig): PivotShape {
  const metricIdx = pc.metrics.map((n) => columns.indexOf(n)).filter((i) => i >= 0);
  const hasExplicitMetricAxis =
    pc.rowDimensions.includes(METRIC_AXIS) || pc.columnDimensions.includes(METRIC_AXIS);
  // Preserve the legacy layout when a multi-metric pivot omitted $metric:
  // metrics become the innermost column axis. With no column dimensions, the
  // metric name is also the natural value-column heading.
  const colDimensions =
    !hasExplicitMetricAxis && (metricIdx.length > 1 || pc.columnDimensions.length === 0)
      ? [...pc.columnDimensions, METRIC_AXIS]
      : pc.columnDimensions;

  const rowKeyMap = new Map<string, string[]>();
  const colKeyMap = new Map<string, string[]>();
  const cells = new Map<string, unknown>();

  const axisValues = (axis: string[], row: unknown[], metricName: string): string[] =>
    axis.map((name) => {
      if (name === METRIC_AXIS) return metricName;
      const idx = columns.indexOf(name);
      return idx >= 0 ? String(row[idx] ?? "") : "";
    });

  for (const row of rows) {
    for (let mi = 0; mi < metricIdx.length; mi++) {
      const idx = metricIdx[mi];
      const metricName = columns[idx];
      const rk = axisValues(pc.rowDimensions, row, metricName);
      const ck = axisValues(colDimensions, row, metricName);
      rowKeyMap.set(joinKey(rk), rk);
      colKeyMap.set(joinKey(ck), ck);
      const cellKey = `${joinKey(rk)}\x1e${joinKey(ck)}`;
      cells.set(cellKey, row[idx]);
    }
  }

  return {
    rowDimNames: pc.rowDimensions,
    colDimNames: colDimensions,
    // Map insertion order preserves the SQL result order. In particular, do
    // not lexicographically sort formatted dates such as 02.08 / 30.08.
    rowKeys: [...rowKeyMap.values()],
    colKeys: [...colKeyMap.values()],
    cells,
  };
}

function formatCell(v: unknown, format: string | undefined, locale: string): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    const language = locale === "ru" ? "ru-RU" : "en-US";
    if (format === "integer") {
      return new Intl.NumberFormat(language, { maximumFractionDigits: 0 }).format(v);
    }
    if (format === "decimal") {
      return new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(v);
    }
    if (format === "percent") {
      return `${new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(v)}%`;
    }
    if (format === "currency") {
      return new Intl.NumberFormat(language, {
        style: "currency",
        currency: "RUB",
        maximumFractionDigits: 0,
      }).format(v);
    }
    return Number.isInteger(v) ? v.toString() : v.toFixed(2);
  }
  return String(v);
}

function compareValues(a: unknown, b: unknown): number {
  if (a === null || a === undefined) {
    if (b === null || b === undefined) return 0;
    return -1;
  }
  if (b === null || b === undefined) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

type SortDir = "asc" | "desc";

// Discriminated sort target: either a row-dimension column (compare by the
// rowKey component at idx) or a pivoted metric column (compare cell values
// at the [rowKey, colKey, metricIdx] coordinate).
type SortTarget =
  | { kind: "rowDim"; idx: number }
  | { kind: "value"; ck: string[] };

function sameTarget(a: SortTarget, b: SortTarget): boolean {
  if (a.kind === "rowDim" && b.kind === "rowDim") return a.idx === b.idx;
  if (a.kind === "value" && b.kind === "value") {
    return joinKey(a.ck) === joinKey(b.ck);
  }
  return false;
}

interface HeaderGroup {
  key: string;
  label: string;
  colSpan: number;
  leaf?: string[];
}

function headerGroups(colKeys: string[][], level: number, depth: number): HeaderGroup[] {
  const groups: HeaderGroup[] = [];
  for (let start = 0; start < colKeys.length; ) {
    const prefix = joinKey(colKeys[start].slice(0, level + 1));
    let end = start + 1;
    while (end < colKeys.length && joinKey(colKeys[end].slice(0, level + 1)) === prefix) {
      end++;
    }
    groups.push({
      key: `${level}-${start}-${prefix}`,
      label: colKeys[start][level] ?? "",
      colSpan: end - start,
      leaf: level === depth - 1 ? colKeys[start] : undefined,
    });
    start = end;
  }
  return groups;
}

export function PivotTable({
  columns,
  rows,
  pivot,
  pagination,
  dimensionLabel = (field) => field,
  metricLabel = (field) => field,
}: Props) {
  const { t, locale } = useLocale();
  const shape = useMemo(() => build(columns, rows, pivot), [columns, rows, pivot]);

  const [sortBy, setSortBy] = useState<{ target: SortTarget; dir: SortDir } | null>(null);

  const sortedRowKeys = useMemo(() => {
    if (!sortBy) return shape.rowKeys;
    const { target, dir } = sortBy;
    const copy = [...shape.rowKeys];
    copy.sort((a, b) => {
      let cmp = 0;
      if (target.kind === "rowDim") {
        cmp = compareValues(a[target.idx], b[target.idx]);
      } else {
        const ckJoin = joinKey(target.ck);
        const aKey = `${joinKey(a)}\x1e${ckJoin}`;
        const bKey = `${joinKey(b)}\x1e${ckJoin}`;
        cmp = compareValues(shape.cells.get(aKey), shape.cells.get(bKey));
      }
      return dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [shape.rowKeys, shape.cells, sortBy]);

  function toggleSort(target: SortTarget) {
    setSortBy((prev) => {
      if (!prev || !sameTarget(prev.target, target)) return { target, dir: "asc" };
      if (prev.dir === "asc") return { target, dir: "desc" };
      return null;
    });
  }

  if ((!rows.length || !shape.colKeys.length) && !pagination) {
    return <div className={styles.empty}>{t("table.no_data")}</div>;
  }

  const headerDepth = Math.max(1, shape.colDimNames.length);

  function arrow(target: SortTarget): string {
    if (!sortBy || !sameTarget(sortBy.target, target)) return "";
    return sortBy.dir === "asc" ? " ▲" : " ▼";
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
        <thead>
          {Array.from({ length: headerDepth }, (_, level) => (
            <tr key={`header-${level}`}>
              {level === 0 && shape.rowDimNames.map((n, i) => {
                const target: SortTarget = { kind: "rowDim", idx: i };
                return (
                  <th key={`rd-${n}`} rowSpan={headerDepth} onClick={() => toggleSort(target)}>
                    <span>{n === METRIC_AXIS ? t("table.metric") : dimensionLabel(n)}{arrow(target)}</span>
                  </th>
                );
              })}
              {shape.colDimNames.length === 0 ? (
                level === 0 && (
                  <th>
                    <span>{t("table.value")}</span>
                  </th>
                )
              ) : (
                headerGroups(shape.colKeys, level, headerDepth).map((group) => {
                  const target: SortTarget | null = group.leaf
                    ? { kind: "value", ck: group.leaf }
                    : null;
                  return (
                    <th
                      key={group.key}
                      colSpan={group.colSpan}
                      onClick={target ? () => toggleSort(target) : undefined}
                    >
                      <span>{shape.colDimNames[level] === METRIC_AXIS ? metricLabel(group.label) : group.label}{target ? arrow(target) : ""}</span>
                    </th>
                  );
                })
              )}
            </tr>
          ))}
        </thead>
        <tbody>
          {sortedRowKeys.map((rk) => (
            <tr key={joinKey(rk)}>
              {rk.map((cell, ci) => (
                <td key={ci}>{shape.rowDimNames[ci] === METRIC_AXIS ? metricLabel(cell) : cell}</td>
              ))}
              {shape.colKeys.map((ck) => {
                const cellKey = `${joinKey(rk)}\x1e${joinKey(ck)}`;
                const rowMetricIndex = shape.rowDimNames.indexOf(METRIC_AXIS);
                const columnMetricIndex = shape.colDimNames.indexOf(METRIC_AXIS);
                const metric = rowMetricIndex >= 0 ? rk[rowMetricIndex] : columnMetricIndex >= 0 ? ck[columnMetricIndex] : pivot.metrics[0];
                return (
                  <td key={cellKey}>
                    {formatCell(shape.cells.get(cellKey), pivot.metricFormats?.[metric], locale)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        </table>
        {rows.length === 0 && (
          <div className={styles.emptyPage}>{t("table.no_data")}</div>
        )}
      </div>
      {pagination ? (
        <TablePagination {...pagination} />
      ) : (
        <p className={styles.meta}>{t("table.rows_count", { count: rows.length })}</p>
      )}
    </div>
  );
}
